import { useEffect, useState } from "react";
import { collection, getDocs, doc, getDoc, updateDoc, setDoc, addDoc, deleteDoc } from "firebase/firestore";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { db } from "../config/firebase";

// ── Defaults (match the hardcoded values in each component) ──────────────────

const HEADER_DEFAULTS = {
  "villas-header": {
    title: "Suites & Villas",
    description: "Discover a private sanctuary where every suite/villa is crafted for serenity, comfort, and unforgettable memories. Wake to ocean breezes, lounge by our pool(s), and gain an out of this world experience in paradise.",
  },
  "wellness-header": {
    title: "Wellness Center",
    description: "Immerse yourself in tranquility with our curated wellness experiences designed to restore balance and rejuvenate your spirit.",
  },
  "pavilion-header": {
    title: "PB Pavilion",
    description: "Experience the heart of community, creativity and culinary craftsmanship. The PB Pavilion hosts unforgettable events, delicious culinary experiences, and moments you'll remember for a lifetime. Reserve your spot now!",
  },
};

const FOOTER_DEFAULTS = {
  email:      "info@agapeprimehotel.com",
  copyright:  "© Agape Prime Hotel, Inc. 2025–Present. All rights reserved.",
  address:    "XXX TBD Road, Belize City, Belize XXXXX",
  phoneFax:   "Phone: 1-877-732-3492 | Fax: 1-877-760-2763 / 1-718-771-5900",
  legalText:  "Copyright. Trademark. Agape Prime, LLC and/or assigns. 2025-present. All related indicia included. All rights reserved.",
  termsUrl:   "https://www.agapeprimehotel.com/WP/terms-of-service/",
  privacyUrl: "https://www.agapeprimehotel.com/WP/privacy-policy/",
};

// Matches faq.js's SECTIONS — the categories a public FAQ item can belong to.
const FAQ_CATEGORY_OPTIONS = [
  { value: "general",  label: "General" },
  { value: "rewards",  label: "Rewards" },
  { value: "hotel",    label: "Hotel" },
  { value: "wellness", label: "Wellness Center" },
  { value: "pavilion", label: "PB Pavilion" },
];

// ── Section config ────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: "villas",
    label: "Suites & Villas",
    headerDocId: "villas-header",
    collection: "villas",
    nameField: "name",
    fields: [
      { key: "name",     label: "Name",         multiline: false },
      { key: "subtitle", label: "Subtitle",      multiline: false },
      { key: "price",    label: "Price / Night", multiline: false },
    ],
  },
  {
    id: "wellness",
    label: "Wellness",
    headerDocId: "wellness-header",
    collection: "wellnessServices",
    nameField: "name",
    fields: [
      { key: "name",        label: "Name",        multiline: false },
      { key: "subtitle",    label: "Type",        multiline: false },
      { key: "description", label: "Description", multiline: true  },
      { key: "price",       label: "Price",       multiline: false },
    ],
  },
  {
    id: "pavilion",
    label: "PB Pavilion",
    headerDocId: "pavilion-header",
    collection: "pavilionEvents",
    nameField: "title",
    fields: [
      { key: "title",       label: "Title",                  multiline: false },
      { key: "price",       label: "Price Per Person ($)",   multiline: false },
      { key: "detailsText", label: "Details (one per line)", multiline: true  },
    ],
  },
  {
    id: "faq",
    label: "FAQ",
    headerDocId: null,
    collection: "faqItems",
    nameField: "q",
    fields: [
      { key: "section", label: "Category", type: "select", options: FAQ_CATEGORY_OPTIONS },
      { key: "q", label: "Question", multiline: false },
      { key: "a", label: "Answer",   multiline: true  },
    ],
  },
  {
    id: "footer",
    label: "Footer",
    headerDocId: null,
    collection: null,
    fields: [
      { key: "email",      label: "Email",           multiline: false },
      { key: "copyright",  label: "Copyright line",  multiline: false },
      { key: "address",    label: "Address",         multiline: false },
      { key: "phoneFax",   label: "Phone / Fax",     multiline: false },
      { key: "legalText",  label: "Legal text",      multiline: true  },
      { key: "termsUrl",   label: "Terms of Service URL",  multiline: false },
      { key: "privacyUrl", label: "Privacy Policy URL",    multiline: false },
    ],
  },
];

// ── Add New card ─────────────────────────────────────────────────────────────

function AddNewCard({ onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="content-card"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        border: `2px dashed ${hovered ? "#0d1b4c" : "#d1d5db"}`,
        background: hovered ? "#f0f4ff" : "#fafafa",
        minHeight: 200,
        gap: 8,
        transition: "border-color 0.15s, background 0.15s",
        boxShadow: "none",
      }}
    >
      <span style={{ fontSize: 40, color: hovered ? "#0d1b4c" : "#9ca3af", lineHeight: 1 }}>+</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: hovered ? "#0d1b4c" : "#6b7280", letterSpacing: "0.02em" }}>
        Add New
      </span>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminContentEditor() {
  const [sectionId, setSectionId] = useState("villas");
  const [items, setItems]         = useState([]);
  const [header, setHeader]       = useState(null);
  const [footer, setFooter]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(null);
  const [saved, setSaved]         = useState(null);
  const [newOpen, setNewOpen]     = useState(false);
  const [newData, setNewData]     = useState({});
  const [creating, setCreating]   = useState(false);

  const cfg = SECTIONS.find((s) => s.id === sectionId);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setItems([]);
      setHeader(null);
      setFooter(null);

      try {
        if (sectionId === "footer") {
          const snap = await getDoc(doc(db, "siteContent", "footer"));
          setFooter(snap.exists() ? { ...FOOTER_DEFAULTS, ...snap.data() } : { ...FOOTER_DEFAULTS });
          return;
        }

        // Load page header
        if (cfg.headerDocId) {
          const defaults = HEADER_DEFAULTS[cfg.headerDocId];
          const snap = await getDoc(doc(db, "siteContent", cfg.headerDocId));
          setHeader(snap.exists()
            ? { title: snap.data().title || defaults.title, description: snap.data().description || defaults.description }
            : { ...defaults });
        }

        // Load listings from the real collection
        const snap = await getDocs(collection(db, cfg.collection));
        let docs = snap.docs.map((d) => {
          const data = d.data();
          if (sectionId === "pavilion") {
            return { id: d.id, ...data, detailsText: (data.details || []).join("\n") };
          }
          return { id: d.id, ...data };
        });
        docs.sort((a, b) => (a.id > b.id ? 1 : -1));
        setItems(docs);
      } catch (err) {
        console.error("AdminContentEditor fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sectionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleItemChange = (id, field, value) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));

  const markSaved = (key) => {
    setSaved(key);
    setTimeout(() => setSaved(null), 2000);
  };

  const handleSaveItem = async (item) => {
    setSaving(item.id);
    try {
      let payload = {};
      if (sectionId === "villas")   payload = { name: item.name, subtitle: item.subtitle, price: item.price };
      if (sectionId === "wellness") payload = { name: item.name, subtitle: item.subtitle, description: item.description, price: item.price };
      if (sectionId === "pavilion") payload = {
        title: item.title,
        price: item.price,
        details: (item.detailsText || "").split("\n").map((l) => l.trim()).filter(Boolean),
      };
      if (sectionId === "faq")      payload = { q: item.q, a: item.a, section: item.section };

      await updateDoc(doc(db, cfg.collection, item.id), payload);
      markSaved(item.id);
    } catch (err) {
      console.error("Save item error:", err);
      alert("Save failed. Check your permissions.");
    } finally {
      setSaving(null);
    }
  };

  const handleSaveHeader = async () => {
    setSaving("header");
    try {
      await setDoc(doc(db, "siteContent", cfg.headerDocId), header);
      markSaved("header");
    } catch (err) {
      console.error("Save header error:", err);
      alert("Save failed. Check your permissions.");
    } finally {
      setSaving(null);
    }
  };

  const handleDeleteItem = async (item) => {
    const label = item[cfg.nameField] || item.id;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, cfg.collection, item.id));
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed. Check your permissions.");
    }
  };

  const handleNewOpen = () => {
    const blank = {};
    cfg.fields.forEach((f) => { blank[f.key] = ""; });
    setNewData(blank);
    setNewOpen(true);
  };

  const handleCreate = async () => {
    if (sectionId === "faq" && !newData.section) {
      alert("Please choose a category before creating this FAQ item.");
      return;
    }
    setCreating(true);
    try {
      let payload = { ...newData };
      if (sectionId === "pavilion") {
        payload.details = (newData.detailsText || "").split("\n").map((l) => l.trim()).filter(Boolean);
        delete payload.detailsText;
      }
      const ref = await addDoc(collection(db, cfg.collection), payload);
      const created = { id: ref.id, ...newData };
      setItems((prev) => [...prev, created]);
      setNewOpen(false);
    } catch (err) {
      console.error("Create error:", err);
      alert("Failed to create. Check your permissions.");
    } finally {
      setCreating(false);
    }
  };

  const handleSaveFooter = async () => {
    setSaving("footer");
    try {
      await setDoc(doc(db, "siteContent", "footer"), footer);
      markSaved("footer");
    } catch (err) {
      console.error("Save footer error:", err);
      alert("Save failed. Check your permissions.");
    } finally {
      setSaving(null);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Section tabs */}
      <div className="admin-filter-row">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            className={`filter-btn ${sectionId === s.id ? "active" : ""}`}
            onClick={() => setSectionId(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="admin-loading">Loading content…</p>
      ) : (
        <>
          {/* ── Footer section ── */}
          {sectionId === "footer" && footer && (
            <div className="content-editor-grid" style={{ gridTemplateColumns: "1fr" }}>
              <div className="content-card">
                <p className="content-card-label" style={{ fontSize: 13, fontWeight: 700, color: "#0d1b4c", marginBottom: 4 }}>
                  Footer Content
                </p>
                {cfg.fields.map((f) => (
                  <div key={f.key}>
                    <p className="content-card-label">{f.label}</p>
                    {f.multiline ? (
                      <textarea
                        className="content-textarea"
                        value={footer[f.key] || ""}
                        onChange={(e) => setFooter((prev) => ({ ...prev, [f.key]: e.target.value }))}
                        rows={3}
                      />
                    ) : (
                      <input
                        className="content-input"
                        value={footer[f.key] || ""}
                        onChange={(e) => setFooter((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
                <button
                  className={`content-save-btn ${saved === "footer" ? "saved" : ""}`}
                  onClick={handleSaveFooter}
                  disabled={saving === "footer"}
                >
                  {saving === "footer" ? "Saving…" : saved === "footer" ? "✓ Saved" : "Save Footer"}
                </button>
              </div>
            </div>
          )}

          {/* ── Hotel sections (header + listings) ── */}
          {sectionId !== "footer" && (
            <div className="content-editor-grid">
              {/* Page header card */}
              {header && (
                <div className="content-card" style={{ borderLeft: "3px solid #0d1b4c" }}>
                  <p className="content-card-label" style={{ fontSize: 13, fontWeight: 700, color: "#0d1b4c", marginBottom: 4 }}>
                    Page Header
                  </p>
                  <p className="content-card-label">Page Title</p>
                  <input
                    className="content-input"
                    value={header.title}
                    onChange={(e) => setHeader((prev) => ({ ...prev, title: e.target.value }))}
                  />
                  <p className="content-card-label">Description</p>
                  <textarea
                    className="content-textarea"
                    value={header.description}
                    onChange={(e) => setHeader((prev) => ({ ...prev, description: e.target.value }))}
                    rows={4}
                  />
                  <button
                    className={`content-save-btn ${saved === "header" ? "saved" : ""}`}
                    onClick={handleSaveHeader}
                    disabled={saving === "header"}
                  >
                    {saving === "header" ? "Saving…" : saved === "header" ? "✓ Saved" : "Save Header"}
                  </button>
                </div>
              )}

              {/* Listing cards */}
              {items.length === 0 && !header && (
                <div className="admin-empty">
                  <p>No listings found in Firestore.</p>
                </div>
              )}

              {items.map((item) => (
                <div key={item.id} className="content-card" style={{ position: "relative" }}>
                  <button
                    onClick={() => handleDeleteItem(item)}
                    title="Delete"
                    style={{
                      position: "absolute", top: 10, right: 10,
                      background: "none", border: "none", cursor: "pointer",
                      color: "#9ca3af", fontSize: 16, lineHeight: 1, padding: 2,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
                  >
                    ✕
                  </button>
                  <p className="content-card-label" style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6, paddingRight: 24 }}>
                    {item[cfg.nameField] || item.id}
                  </p>
                  {cfg.fields.map((f) => (
                    <div key={f.key}>
                      <p className="content-card-label">{f.label}</p>
                      {f.type === "select" ? (
                        <select
                          className="content-input"
                          value={item[f.key] || ""}
                          onChange={(e) => handleItemChange(item.id, f.key, e.target.value)}
                        >
                          <option value="" disabled>Select a category…</option>
                          {f.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : f.multiline ? (
                        <textarea
                          className="content-textarea"
                          value={item[f.key] || ""}
                          onChange={(e) => handleItemChange(item.id, f.key, e.target.value)}
                          rows={3}
                        />
                      ) : (
                        <input
                          className="content-input"
                          value={item[f.key] || ""}
                          onChange={(e) => handleItemChange(item.id, f.key, e.target.value)}
                        />
                      )}
                    </div>
                  ))}
                  <button
                    className={`content-save-btn ${saved === item.id ? "saved" : ""}`}
                    onClick={() => handleSaveItem(item)}
                    disabled={saving === item.id}
                  >
                    {saving === item.id ? "Saving…" : saved === item.id ? "✓ Saved" : "Save Changes"}
                  </button>
                </div>
              ))}

              {/* + Add New card */}
              <AddNewCard onClick={handleNewOpen} />
            </div>
          )}

          {/* New item dialog */}
          <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, color: "#0d1b4c" }}>
              Add New {cfg.label} Item
            </DialogTitle>
            <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 2 }}>
              {cfg.fields.map((f) => (
                <div key={f.key}>
                  <p className="content-card-label">{f.label}</p>
                  {f.type === "select" ? (
                    <select
                      className="content-input"
                      value={newData[f.key] || ""}
                      onChange={(e) => setNewData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    >
                      <option value="" disabled>Select a category…</option>
                      {f.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  ) : f.multiline ? (
                    <textarea
                      className="content-textarea"
                      value={newData[f.key] || ""}
                      onChange={(e) => setNewData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      rows={3}
                    />
                  ) : (
                    <input
                      className="content-input"
                      value={newData[f.key] || ""}
                      onChange={(e) => setNewData((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setNewOpen(false)} disabled={creating} sx={{ color: "#6b7280" }}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={creating || (sectionId === "faq" && !newData.section)}
                variant="contained"
                sx={{ backgroundColor: "#0d1b4c", "&:hover": { backgroundColor: "#1a2d5a" } }}
              >
                {creating ? "Creating…" : "Create"}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
}
